import Button from './Button';
import Row from './Grid/Row';
import Noimg from '@/assets/img/add_img@2x.png';
import iconAttachmentX from '@/assets/img/icons/ico_preview_attach_delete@2x.png';
import { breakpoint, breakpointReverse } from '@/helpers/BreakpointHelper';
import { usePopupStore } from '@/stores/PopupStore';
import { observer } from 'mobx-react-lite';
import React, {
  ChangeEventHandler,
  FC,
  InputHTMLAttributes,
  useCallback,
  useMemo,
} from 'react';
import styled from 'styled-components';

const AttachmentContainer = styled(Row)`
  margin: 0 -6px;
  align-items: flex-start;
  
  &.fullsize{
    display:block;
  }

  .preview {
    padding: 0 6px;
    height : 366px;

    ${breakpointReverse(`mobile`)} {
     &.col-left {
        width: 366px;
      }
    }
    
    ${Row}.file-review {
      border: 1px solid #e5e6e8;
      width: 100%;
      height: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      color: #b7b8ba;
      text-align: center;
      display:inline-flex;
      background:url(${Noimg}) no-repeat center;

      img {
        max-width: 100%;
        max-height: 100%;
        object-fit: contain;
        vertical-align: middle;
      }
    }
  }

  .uploader {
    display: block;
    align-items: center;
    margin : 20px 0 0 6px;
    ${breakpointReverse(`mobile`)} {
      &.col-right {
        margin : 0 0 0 30px;
        padding-top : 290px;
        width: calc(100% - 366px);
        height : 366px;
      }
    }
  }
}
  ${Button} {
      background: #fff;
      border-color: #00aeef;
      color: #00aeef;
      font-size:16px;
      z-index: -1;
      ${breakpointReverse(`mobile`)} {
        &:not(:disabled):hover {
          color: #fff;
          border-color: #00aeef;
          background-color: #00aeef;
        }
      }
    }
  }

  ${breakpoint(`mobile`)} {
    display:inline;

    .preview {
      display:block;
      width: 100%;
      height: 100vw;
      padding: 0;
    }
    .uploader {
      padding-top : 0;
      margin: 20px 0 0 0;
      display:block;
      width: 100%;
    }
    ${Button} {
      padding: 15px 50px;
    }
  }
`;

const InputFile = styled.input`
  display: none;
`;
const Label = styled.label`
  display: block;
  position: relative;
  z-index: 1;
`;
const FilePreviewImage = styled.div`
  border: 1px solid #e5e6e8;
  width: 100%;
  height: 100%;
  display: inline-block;
  position: relative;
  button {
    position: absolute;
    right: 4px;
    top: 4px;
    img {
      border: solid 1px rgb(255 255 255 / 50%);
      border-radius: 50%;
    }
  }
  span {
    display: inline-flex;
    width: 100%;
    height: 100%;
    overflow: hidden;
    img {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
      vertical-align: middle;
    }
  }
`;
const InputFileNoti = styled.div`
  ${breakpoint(`mobile`)} {
    margin-top: 10px;
  }
`;

interface AttachmentProps extends InputHTMLAttributes<HTMLInputElement> {
  maxFileCount?: number;
  files: File[];
  onFilesChange: (files: File[]) => void;
  defaultImg?: string;
  fullsize?: boolean;
}

const AttachmentPreview: FC<AttachmentProps> = observer(
  ({
    maxFileCount = 5,
    files,
    onFilesChange,
    defaultImg,
    fullsize = false,
    ...props
  }) => {
    const popupStore = usePopupStore();
    // 첨부파일 지정
    const handleAttachmentFile: ChangeEventHandler<HTMLInputElement> = useCallback(
      (event) => {
        // null 인 경우
        if (!event.target.files) {
          return;
        }
        // 확장자 검증
        const pattern = /image-*/;
        const maxSize = 5 * 1024 * 1024;
        let isImage = true;
        let isOverflow = false;

        Array.from(event.target.files).forEach((file) => {
          // 확장자 체크
          if (!file.type.match(pattern)) {
            isImage = false;
          }
          // 용량체크
          if (file.size > maxSize) {
            isOverflow = true;
          }
        });
        if (!isImage) {
          popupStore.show(`이미지 파일만 등록 가능합니다.`);
          return;
        }
        if (isOverflow) {
          popupStore.show(`첨부파일 사이즈는 5MB 이내로 등록 가능합니다`);
          return;
        }

        const newFiles = [...files, ...Array.from(event.target.files)];
        // 최대 갯수체크
        if (newFiles.length > maxFileCount) {
          popupStore.show(`최대 ${maxFileCount}개 까지 업로드 가능합니다`);
        }
        const finalFiles = newFiles.slice(0, maxFileCount);

        onFilesChange(finalFiles);

        // 파일 input 초기화
        // eslint-disable-next-line no-param-reassign
        event.target.value = '';
      },
      [files, maxFileCount, onFilesChange, popupStore],
    );

    const removeFile = (file: File) =>
      onFilesChange(files.filter((f) => f !== file));

    const images = useMemo(
      () => files.map((file) => URL.createObjectURL(file)),
      [files],
    );

    return (
      <AttachmentContainer className={fullsize === true ? `fullsize` : ``}>
        <div className={`preview ${fullsize === true ? `` : `col-left`}`}>
          {files.length > 0 ? (
            files.map((file, i) => (
              <FilePreviewImage key={file.name}>
                <span>
                  <img src={images[i]} alt={file.name} />
                </span>
                <button type="button" onClick={() => removeFile(file)}>
                  <img
                    src={iconAttachmentX}
                    alt="X"
                    width="24"
                    height="24"
                    css={`
                      cursor: pointer;
                    `}
                  />
                </button>
              </FilePreviewImage>
            ))
          ) : (
            <Row className="file-review">
              {defaultImg ? (
                <img src={defaultImg} alt="기념기부 이미지" />
              ) : (
                <></>
              )}
              {/* 이미지 미리보기 */}
            </Row>
          )}
        </div>
        <div className={`uploader ${fullsize === true ? `` : `col-right`}`}>
          <Label>
            <InputFile
              type="file"
              onChange={handleAttachmentFile}
              {...props}
              accept="image/png, image/gif, image/jpeg"
            />
            <Button type="button">사진 업로드</Button>
          </Label>
          <InputFileNoti>gif, jpg, jpeg, png 파일 형식</InputFileNoti>
        </div>
      </AttachmentContainer>
    );
  },
);

export default AttachmentPreview;
